import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  Canvas,
  Circle,
  Color,
  Path,
  Point,
  Rect,
  Triangle,
  Polyline,
} from 'fabric';

import { DrawData } from '../draw.constants';
import {
  Tool,
  ToolSet,
  Shape,
  DRAW_OBJECT_TYPE_PATH,
  DRAW_OBJECT_TYPE_TEXTBOX,
  DRAW_OBJECT_TYPE_CIRCLE,
  DRAW_OBJECT_TYPE_TRIANGLE,
  DRAW_OBJECT_TYPE_RECT,
  DRAW_OBJECT_TYPE_POLYLINE,
  DRAW_OBJECT_TYPE_IMAGE,
  INSTRUCTION_ELEMENT_ID,
  defaultPencil,
  OBJECT_SOURCE_KEY_QUESTION,
} from './draw-canvas.constants';
import {
  resetZoomAndViewport,
  increaseZoom,
  decreaseZoom,
  onPathCreated,
  getTouchEvent,
  dragAddingShape,
} from './draw.utils';
import { DrawHistory } from './logic/draw-history';
import {
  addArtboard,
  addCircle,
  addLine,
  addText,
  addTriangle,
  addRectangle,
  addSquare,
} from './logic/draw-objects';
import {
  initialiseAndPositionCanvas,
  reviveSavedObjects,
} from './logic/draw-revive';
import { checkCanvasLimits } from './logic/check-canvas-limits';
import { getDataToSave } from './logic/get-data-to-save';
import { getMergedDrawObjects } from './logic/get-merged-draw-objects';
import { handleMouseWheelEvent } from './logic/handle-mouse-wheel-event';
import { BACKSPACE, DELETE, Z } from 'approot/shared/ally/keyboard.hooks';
import type { InteractiveSubActivityFragment } from '../../../lesson-builder.graphql.generated';
import { useDrawCanvasContext } from './draw-canvas.context';
import { isMacPlatform } from 'approot/shared/ally/is-mac-platform';
import { JSONObject } from 'global/graphql/scalars.type';
import { LanguageCodes } from 'global/constants';

const CANVAS_CONTAINER_ID = 'canvas-container';
const FABRIC_ELEMENT_ID = 'fabric-container';

type UseDrawCanvasProps = {
  id: string;
  type: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  subActivityProperties?: InteractiveSubActivityFragment | null;
  answer?: DrawData | null;
  questionData?: DrawData | null;
  isCreateMode: boolean;
  onSave?: (content: DrawData) => void;
  canvasElement?: HTMLCanvasElement | null; // For edit mode
  canvasElementId?: string; // For regular mode (defaults to FABRIC_ELEMENT_ID)
  language: LanguageCodes;
};

export const useDrawCanvas = ({
  imageUrl,
  dimensionsData,
  answer,
  questionData,
  isCreateMode,
  language,
  onSave,
  canvasElement,
  canvasElementId = FABRIC_ELEMENT_ID,
}: UseDrawCanvasProps) => {
  const fabricRef = useRef<Canvas | null>(null);
  const toolRef = useRef<ToolSet | null>(null);
  const historyRef = useRef<DrawHistory>(undefined);
  const shapeColourRef = useRef<{
    strokeColour: string;
    fillColour: string;
  }>({
    strokeColour: '',
    fillColour: '',
  });

  const [isCursorClosed, setIsCursorClosed] = useState<boolean>(false);
  const [isInitialised, setInitialised] = useState<boolean>(false);
  const [historyCount, setHistoryCount] = useState<{
    position: number;
    stackLength: number;
  }>({
    position: 0,
    stackLength: 0,
  });
  const [limit, setLimit] = useState<{
    isWarningReached: boolean;
    isLimitReached: boolean;
  }>({
    isLimitReached: false,
    isWarningReached: false,
  });
  const [zoom, setZoom] = useState<number>(1);

  const {
    toolSet,
    setToolSet,
    pencil,
    setPencil,
    setupCustomCursor,
    setPencilDrawing,
    strokeColour,
    fillColour,
  } = useDrawCanvasContext();

  // Determine the data source - prioritize drawingData, fallback to merged answer/questionData
  const finalDrawingData = useMemo(() => {
    return getMergedDrawObjects(answer, questionData);
  }, [answer, questionData]);

  const hasDrawing = useMemo(
    () =>
      finalDrawingData?.objects?.some(
        (obj: JSONObject) => obj.type !== 'Image'
      ),
    [finalDrawingData]
  );

  const exposeCanvasData = useCallback(() => {
    if (!fabricRef.current) return null;
    const canvasWrapperEl = document.getElementById(CANVAS_CONTAINER_ID);
    const viewportWidth = canvasWrapperEl?.offsetWidth || 0;
    const viewportHeight = canvasWrapperEl?.offsetHeight || 0;
    return getDataToSave({
      canvas: fabricRef.current,
      dimensions: {
        width: viewportWidth,
        height: viewportHeight,
      },
      isCreateMode,
    });
  }, [isCreateMode]);

  useEffect(() => {
    const targetElement =
      canvasElement || document.getElementById(canvasElementId);
    if (!targetElement) return;

    const canvasWrapperEl = document.getElementById(CANVAS_CONTAINER_ID);
    const instructionEl = document.getElementById(INSTRUCTION_ELEMENT_ID);
    const viewportWidth = canvasWrapperEl?.offsetWidth || 0;
    const viewportHeight = canvasWrapperEl?.offsetHeight || 0;
    const instructionHeight = instructionEl?.offsetHeight || 0;

    const canvas = new Canvas(targetElement as HTMLCanvasElement, {
      selection: false,
      width: viewportWidth,
      height: viewportHeight,
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;

    const artboard = addArtboard(canvas, dimensionsData);

    // Initialize pencil drawing for regular mode
    if (!isCreateMode && setPencilDrawing) {
      setPencilDrawing(canvas, pencil);
    }

    const cleanUpCustomCursor = setupCustomCursor(canvas, pencil);

    const setZoomState = debounce((zoom: number) => {
      setZoom(zoom);
    }, 300);

    function onDrawChange(position: number, stackLength: number) {
      setHistoryCount({
        position,
        stackLength,
      });

      const limits = checkCanvasLimits(canvas);
      setLimit(limits);

      if (!limits.isLimitReached) {
        onSave?.(
          getDataToSave({
            canvas,
            dimensions: {
              width: viewportWidth,
              height: viewportHeight,
            },
            isCreateMode,
          })
        );
      }
    }

    function initialiseCanvas() {
      // handle existing drawing data
      if (finalDrawingData?.objects?.length) {
        reviveSavedObjects({
          canvas,
          artboard,
          drawingData: finalDrawingData,
          isCreateMode,
          language,
        }).then(() =>
          initialiseAndPositionCanvas(
            canvas,
            dimensionsData,
            imageUrl,
            artboard,
            instructionHeight
          ).then(() => {
            historyRef.current = new DrawHistory({
              canvas,
              callbacks: {
                onChange: onDrawChange,
              },
              isCreateMode,
            });
            setLimit(checkCanvasLimits(canvas));
            setInitialised(true);
          })
        );
      } else {
        initialiseAndPositionCanvas(
          canvas,
          dimensionsData,
          imageUrl,
          artboard,
          instructionHeight
        ).then(() => {
          historyRef.current = new DrawHistory({
            canvas,
            callbacks: {
              onChange: onDrawChange,
            },
            isCreateMode,
          });
          setLimit(checkCanvasLimits(canvas));
          setInitialised(true);
        });
      }
    }

    initialiseCanvas();

    let isPanning = false;
    let lastPosX = 0;
    let lastPosY = 0;
    let mouseDragPointStart: Point | undefined;
    let mouseDraggingShape: boolean = false;
    let currentShape: Circle | Triangle | Rect | Polyline | null = null;

    canvas.on('path:created', async function (opt: any) {
      if ('path' in opt) {
        onPathCreated(canvas, opt.path as Path);
        canvas.requestRenderAll();
      }
    });

    canvas.on('mouse:down', function (evt: any) {
      if (toolRef.current?.tool === Tool.Cursor) {
        const touchEvent = getTouchEvent(evt);
        isPanning = true;
        lastPosX = touchEvent.clientX;
        lastPosY = touchEvent.clientY;
        setIsCursorClosed(true);
      } else if (toolRef.current?.tool === Tool.Shapes) {
        mouseDraggingShape = true;
        mouseDragPointStart = canvas.getScenePoint(evt);
      }
    });

    canvas.on('mouse:move', async function (evt: any) {
      if (isPanning) {
        const vpt = canvas.viewportTransform;
        const touchEvent = getTouchEvent(evt);
        vpt[4] += touchEvent.clientX - lastPosX;
        vpt[5] += touchEvent.clientY - lastPosY;
        canvas.requestRenderAll();
        lastPosX = touchEvent.clientX;
        lastPosY = touchEvent.clientY;
        setIsCursorClosed(true);
      } else if (mouseDraggingShape && mouseDragPointStart) {
        const pointer = canvas.getScenePoint(evt);
        // If there is already a shape, drag it
        if (currentShape) {
          dragAddingShape(pointer, canvas, currentShape, mouseDragPointStart);
        } else {
          // If there is no shape, add a new one
          canvas.discardActiveObject();
          const shapeColour = shapeColourRef.current;
          if (toolRef.current?.shape === Shape.Circle) {
            // Only add a circle if the radius is greater than 1 to stop flashing
            const radius = Math.abs(pointer.x - mouseDragPointStart.x);
            if (radius > 1) {
              // In this case, we are dragging a triangle, so we don't want to save changes
              historyRef.current?.dontListenForChanges();
              currentShape = await addCircle({
                canvas,
                point: pointer,
                strokeColour: shapeColour.strokeColour,
                fillColour: shapeColour.fillColour,
                radius,
              });
            }
          } else if (toolRef.current?.shape === Shape.Triangle) {
            // Only add a triangle if the width and height are greater than 1 to stop flashing
            const width = Math.abs(mouseDragPointStart.x - pointer.x);
            const height = Math.abs(mouseDragPointStart.y - pointer.y);
            if (width > 1 && height > 1) {
              // In this case, we are dragging a triangle, so we don't want to save changes
              historyRef.current?.dontListenForChanges();
              currentShape = await addTriangle({
                canvas,
                point: pointer,
                strokeColour: shapeColour.strokeColour,
                fillColour: shapeColour.fillColour,
                width,
                height,
              });
            }
          } else if (toolRef.current?.shape === Shape.Square) {
            const width = Math.abs(pointer.x - mouseDragPointStart.x);
            const height = Math.abs(pointer.y - mouseDragPointStart.y);
            const size = Math.max(width, height);
            if (size > 1) {
              historyRef.current?.dontListenForChanges();
              currentShape = await addSquare({
                canvas,
                point: pointer,
                strokeColour: shapeColour.strokeColour,
                fillColour: shapeColour.fillColour,
                size,
              });
            }
          } else if (toolRef.current?.shape === Shape.Rectangle) {
            const width = Math.abs(mouseDragPointStart.x - pointer.x);
            const height = Math.abs(mouseDragPointStart.y - pointer.y);
            if (width > 1 && height > 1) {
              historyRef.current?.dontListenForChanges();
              currentShape = await addRectangle({
                canvas,
                point: pointer,
                strokeColour: shapeColour.strokeColour,
                fillColour: shapeColour.fillColour,
                width,
                height,
              });
            }
          } else if (toolRef.current?.shape === Shape.Line) {
            // check if movement in either direction exceeds 1 pixel
            if (
              Math.abs(pointer.x - mouseDragPointStart.x) > 1 ||
              Math.abs(pointer.y - mouseDragPointStart.y) > 1
            ) {
              historyRef.current?.dontListenForChanges();
              currentShape = await addLine({
                canvas,
                point: mouseDragPointStart,
                strokeColour: shapeColour.strokeColour,
                x1: mouseDragPointStart.x,
                y1: mouseDragPointStart.y,
                x2: pointer.x,
                y2: pointer.y,
              });
            }
          }
        }
      }
    });

    canvas.on('mouse:up', async function (evt: any) {
      const type = evt.target?.type;
      const toolSet = toolRef.current;
      mouseDraggingShape = false;

      if (
        (type === DRAW_OBJECT_TYPE_CIRCLE ||
          type === DRAW_OBJECT_TYPE_TRIANGLE ||
          type === DRAW_OBJECT_TYPE_TEXTBOX ||
          type === DRAW_OBJECT_TYPE_PATH ||
          type === DRAW_OBJECT_TYPE_POLYLINE ||
          (type === DRAW_OBJECT_TYPE_RECT &&
            !(evt.target as any)?.isArtboard)) &&
        (toolSet?.tool === undefined || toolSet?.tool === Tool.Select)
      ) {
        setToolSet({ tool: Tool.Select });
        const activeObject = canvas.getActiveObject();
        if (type === DRAW_OBJECT_TYPE_PATH && activeObject) {
          // Need to convert the saved rgb colour to a hex value
          const colour = activeObject?.get('stroke');
          const colourHex = `#${new Color(colour).toHex()}`;
          setPencil({
            colour: colourHex,
            size: activeObject?.get('strokeWidth') || defaultPencil.size,
          });
        }
      } else if (toolSet?.tool === Tool.Text) {
        const textOptions = isCreateMode
          ? {
              canvas,
              point: canvas.getScenePoint(evt),
              objectSource: OBJECT_SOURCE_KEY_QUESTION,
            }
          : { canvas, point: canvas.getScenePoint(evt) };
        addText(textOptions);
        setToolSet({ tool: Tool.Select });
      } else if (toolSet?.tool === Tool.Shapes) {
        // Handles adding a shape on single click
        if (mouseDragPointStart && !currentShape) {
          const pointer = canvas.getScenePoint(evt);
          const shapeColour = shapeColourRef.current;
          if (toolRef.current?.shape === Shape.Circle) {
            currentShape = await addCircle({
              canvas,
              point: pointer,
              strokeColour: shapeColour.strokeColour,
              fillColour: shapeColour.fillColour,
            });
          } else if (toolRef.current?.shape === Shape.Triangle) {
            currentShape = await addTriangle({
              canvas,
              point: pointer,
              strokeColour: shapeColour.strokeColour,
              fillColour: shapeColour.fillColour,
            });
          } else if (toolRef.current?.shape === Shape.Square) {
            currentShape = await addSquare({
              canvas,
              point: pointer,
              strokeColour: shapeColour.strokeColour,
              fillColour: shapeColour.fillColour,
            });
          } else if (toolRef.current?.shape === Shape.Rectangle) {
            currentShape = await addRectangle({
              canvas,
              point: pointer,
              strokeColour: shapeColour.strokeColour,
              fillColour: shapeColour.fillColour,
            });
          } else if (toolRef.current?.shape === Shape.Line) {
            currentShape = await addLine({
              canvas,
              point: pointer,
              strokeColour: shapeColour.strokeColour,
            });
          }
        }

        // Set the newly added shape as the active object
        if (currentShape) {
          canvas.setActiveObject(currentShape);
          canvas.requestRenderAll();
          currentShape = null;
        }

        // Save the canvas state when a shape is added
        historyRef.current?.saveCanvasState();
        historyRef.current?.listenForChanges();

        setToolSet({
          tool: Tool.Select,
        });
      } else if (
        toolSet?.tool !== Tool.Eraser &&
        toolSet?.tool !== Tool.Pencil &&
        toolSet?.tool !== Tool.Cursor &&
        (type === DRAW_OBJECT_TYPE_IMAGE ||
          !type ||
          (type === DRAW_OBJECT_TYPE_RECT && (evt.target as any)?.isArtboard))
      ) {
        setToolSet(null);
      }

      canvas.setViewportTransform(canvas.viewportTransform);
      isPanning = false;
      setIsCursorClosed(false);
    });

    canvas.on('mouse:wheel', function (opt: any) {
      handleMouseWheelEvent({
        opt,
        canvas,
        setZoomState,
      });
    });

    const el = canvas.getSelectionElement();

    function handleMovePointer(event: any) {
      if (
        canvas.isDrawingMode ||
        canvas.getActiveObject() ||
        toolRef.current?.tool === Tool.Shapes
      )
        return;

      if (isPanning && event.pointerType === 'touch' && event.isPrimary) {
        const deltaX = event.clientX - lastPosX;
        const deltaY = event.clientY - lastPosY;

        lastPosX = event.clientX;
        lastPosY = event.clientY;

        const currentViewport = canvas.viewportTransform;
        if (currentViewport) {
          currentViewport[4] += deltaX;
          currentViewport[5] += deltaY;
          canvas.setViewportTransform(currentViewport);
          canvas.requestRenderAll();
        }
      }
    }

    el.addEventListener('pointerdown', (event: any) => {
      if (
        canvas.isDrawingMode ||
        canvas.getActiveObject() ||
        toolRef.current?.tool === Tool.Shapes
      )
        return;

      if (event.pointerType === 'touch' && event.isPrimary) {
        isPanning = true;
        lastPosX = event.clientX;
        lastPosY = event.clientY;

        el.addEventListener('pointermove', handleMovePointer);
        event.preventDefault();
      }
    });

    el.addEventListener('pointerup', () => {
      isPanning = false;
      el.removeEventListener('pointermove', handleMovePointer);
    });

    el.addEventListener('pointercancel', () => {
      isPanning = false;
    });

    el.addEventListener('pointerout', () => {
      isPanning = false;
    });

    // Delete selected object with backspace or delete key or undo with ctrl/cmd + z
    function handleKeyDown(event: any) {
      const type = event.target.type;
      if (
        type !== 'textarea' &&
        (event.key === BACKSPACE || event.key === DELETE)
      ) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          canvas.remove(activeObject);
          canvas.requestRenderAll();
        }
      }

      const isMac = isMacPlatform();

      if ((isMac ? event.metaKey : event.ctrlKey) && event.key === Z) {
        historyRef.current?.undo(language);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Disable default touch action
    el.style.touchAction = 'none';

    const onResize = () => {
      const element = document.getElementById(CANVAS_CONTAINER_ID);
      if (element) {
        canvas.setDimensions({
          width: element.offsetWidth,
          height: element.offsetHeight,
        });
      }
      // should do zoom to fit instead here
      setZoom(resetZoomAndViewport(canvas, viewportWidth));
    };

    window.addEventListener('resize', onResize, false);

    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', handleKeyDown);
      cleanUpCustomCursor?.();

      // Dispose canvas for edit mode
      if (isCreateMode && fabricRef.current) {
        fabricRef.current.dispose();
        fabricRef.current = null;
      }
    };
    //eslint-disable-next-line
  }, [canvasElement, canvasElementId]);

  // keep the current tool in sync
  useEffect(() => {
    toolRef.current = toolSet;
  }, [toolSet, toolSet?.tool, toolSet?.shape]);

  useEffect(() => {
    shapeColourRef.current = {
      strokeColour,
      fillColour,
    };
  }, [strokeColour, fillColour]);

  useEffect(() => {
    if (fabricRef.current) {
      fabricRef.current.zoomToPoint(
        new Point(
          (fabricRef.current.width || 0) / 2,
          (fabricRef.current.height || 0) / 2
        ),
        zoom
      );
    }
    // eslint-disable-next-line
  }, [zoom]);

  return {
    canvas: fabricRef.current,
    history: historyRef.current,
    isInitialised,
    hasDrawing,
    historyCount,
    limit,
    zoom,
    setZoom,
    isCursorClosed,
    // Action functions
    increaseZoom: () => setZoom(increaseZoom(zoom)),
    decreaseZoom: () => setZoom(decreaseZoom(zoom)),
    undo: () => historyRef.current?.undo(language),
    redo: () => historyRef.current?.redo(language),
    getCanvasData: exposeCanvasData,
  };
};
